import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { FileText, Loader2, Moon, Plus, Search, Share2, Users, Zap, Sun } from 'lucide-react';

import { DocCard } from '@/components/dashboard/DocCard';
import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDocActions } from '@/hooks/useDocActions';
import { useDocFavorites } from '@/hooks/useDocFavorites';
import { useDocList } from '@/hooks/useDocList';
import { useTheme } from '@/hooks/useTheme';
import type { DocSummary } from '@/lib/doc/types';
import { docKey as makeDocKey } from '@/lib/doc/storage';
import { cn } from '@/lib/utils';

type Filter = 'all' | 'starred' | 'shared';

export default function Dashboard() {
  const { user } = useCurrentUser();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { documents, shared, isLoading } = useDocList();
  const { isFavorite, toggle } = useDocFavorites(user?.pubkey);
  const actions = useDocActions();

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DocSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DocSummary | null>(null);

  useSeoMeta({
    title: 'Nostr Office — Collaborative documents on Nostr',
    description: 'A decentralized, real-time collaborative document editor powered by your Nostr identity.',
  });

  // Refresh the list whenever the dashboard is (re)opened, e.g. after
  // leaving the editor.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['nostr', 'doc-list'] });
  }, [queryClient]);

  const visible = useMemo(() => {
    let docs = documents;
    if (filter === 'starred') {
      docs = documents.filter((doc) => isFavorite(makeDocKey(doc.ownerPubkey, doc.docId)));
    } else if (filter === 'shared') {
      docs = shared;
    }
    const query = search.trim().toLowerCase();
    if (query) {
      docs = docs.filter((doc) => doc.title.toLowerCase().includes(query));
    }
    return docs;
  }, [documents, shared, filter, search, isFavorite]);

  const starredCount = useMemo(
    () => documents.filter((doc) => isFavorite(makeDocKey(doc.ownerPubkey, doc.docId))).length,
    [documents, isFavorite],
  );

  const createDocument = async () => {
    setCreating(true);
    try {
      const doc = await actions.createDocument();
      if (doc) {
        navigate(`/doc/${nip19.npubEncode(doc.ownerPubkey)}/${doc.docId}`);
      }
    } catch (error) {
      console.warn('[nostr-office] create failed', error);
      toast({ title: 'Could not create the document', description: 'Check your connection and try again.', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const confirmRename = async () => {
    if (!renameTarget) return;
    const ok = await actions.renameDocument(renameTarget, renameValue.trim() || 'Untitled document');
    if (ok) {
      toast({ title: 'Document renamed' });
    } else {
      toast({ title: 'Rename failed', variant: 'destructive' });
    }
    setRenameTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await actions.deleteDocument(deleteTarget);
    if (ok) {
      toast({ title: 'Document deleted' });
    } else {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
    setDeleteTarget(null);
  };

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              <FileText className="h-5 w-5" aria-hidden />
            </div>
            <div className="leading-tight">
              <p className="text-[15px] font-bold tracking-tight">Nostr Office</p>
              <p className="hidden text-xs text-muted-foreground sm:block">Collaborative documents</p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="h-9 w-9"
            >
              {theme === 'dark' ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
            </Button>
            <LoginArea className="max-w-56" />
            {user && (
              <Button onClick={() => void createDocument()} disabled={creating} className="gap-1.5">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="hidden sm:inline">New Document</span>
                <span className="sm:hidden">New</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-24 pt-8 sm:px-6">
        {!user ? (
          <Landing />
        ) : (
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-1 rounded-lg bg-muted p-1 text-sm font-medium">
                {(['all', 'starred', 'shared'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilter(value)}
                    className={cn(
                      'rounded-md px-3 py-1.5 transition-colors',
                      filter === value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-current={filter === value}
                  >
                    {value === 'all' ? 'All documents'
                      : value === 'starred' ? `Starred${starredCount ? ` (${starredCount})` : ''}`
                      : `Shared with me${shared.length ? ` (${shared.length})` : ''}`}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search documents"
                  className="pl-9"
                  aria-label="Search documents"
                />
              </div>
            </div>

            <section className="mt-6" aria-label="Document list">
              {isLoading ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton key={index} className="h-28 rounded-xl" />
                  ))}
                </div>
              ) : visible.length === 0 ? (
                <EmptyState filter={filter} search={search} onCreate={() => void createDocument()} creating={creating} />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visible.map((doc) => (
                    <DocCard
                      key={`${doc.ownerPubkey}:${doc.docId}`}
                      doc={doc}
                      viewerPubkey={user.pubkey}
                      isFavorite={isFavorite(makeDocKey(doc.ownerPubkey, doc.docId))}
                      onToggleFavorite={() => toggle(makeDocKey(doc.ownerPubkey, doc.docId))}
                      onRename={() => {
                        setRenameTarget(doc);
                        setRenameValue(doc.title);
                      }}
                      onDuplicate={() => {
                        void actions.duplicateDocument(doc).then((copy) => {
                          if (copy) toast({ title: 'Duplicated', description: copy.title });
                          else toast({ title: 'Could not duplicate', variant: 'destructive' });
                        });
                      }}
                      onDelete={() => setDeleteTarget(doc)}
                    />
                  ))}
                </div>
              )}
            </section>

            <footer className="mt-16 text-center text-xs text-muted-foreground">
              <p>
                Documents sync across multiple Nostr relays · offline edits merge automatically ·
                content is not yet end-to-end encrypted
              </p>
              <p className="mt-1">
                Vibed with{' '}
                <a
                  href="https://shakespeare.diy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Shakespeare
                </a>
              </p>
            </footer>
          </>
        )}
      </main>

      <Dialog open={renameTarget !== null} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
            <DialogDescription>Give the document a new title.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-input">Title</Label>
            <Input
              id="rename-input"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void confirmRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={() => void confirmRename()}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The document will be removed from your list and a deletion request will be
              broadcast to the relays. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void confirmDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Landing() {
  return (
    <div className="mx-auto max-w-2xl py-12 text-center sm:py-20">
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
        <FileText className="h-8 w-8" aria-hidden />
      </div>
      <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
        Documents on Nostr
      </h1>
      <p className="mx-auto mt-4 max-w-xl text-balance text-lg text-muted-foreground">
        A real-time collaborative document editor that runs on Nostr relays.
        Your keys, your data — no accounts, no servers.
      </p>
      <div className="mt-8 flex flex-col items-center gap-3">
        <LoginArea className="w-full max-w-xs [&>button]:w-full [&>button]:h-11" />
        <p className="text-xs text-muted-foreground">
          Log in with a NIP-07 extension, secret key, or remote signer
        </p>
      </div>

      <div className="mx-auto mt-14 grid max-w-lg gap-4 text-left sm:grid-cols-3">
        <Feature icon={Zap} title="Real-time">
          Live cursors, presence and conflict-free merging of concurrent edits.
        </Feature>
        <Feature icon={Share2} title="Permissioned">
          Share with any Nostr user as editor or viewer using their npub.
        </Feature>
        <Feature icon={Users} title="Decentralized">
          Synced across multiple public relays. Works offline, merges on reconnect.
        </Feature>
      </div>

      <p className="mt-12 text-xs text-muted-foreground">
        MVP note: documents are stored unencrypted on public relays — end-to-end
        encryption is on the roadmap.
      </p>
    </div>
  );
}

function Feature({ icon: Icon, title, children }: { icon: typeof Zap; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <Icon className="mb-2 h-5 w-5 text-primary" aria-hidden />
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function EmptyState({
  filter,
  search,
  onCreate,
  creating,
}: {
  filter: Filter;
  search: string;
  onCreate: () => void;
  creating: boolean;
}) {
  return (
    <div className="rounded-xl border border-dashed bg-card/50 px-8 py-16 text-center">
      <FileText className="mx-auto mb-4 h-10 w-10 text-muted-foreground/50" aria-hidden />
      <p className="font-medium">
        {search
          ? 'No documents match your search.'
          : filter === 'shared'
            ? 'No documents have been shared with you yet.'
            : filter === 'starred'
              ? 'No starred documents yet.'
              : 'Create your first document'}
      </p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
        {search
          ? 'Try a different search term.'
          : 'Documents live on Nostr relays and sync in real time with your collaborators.'}
      </p>
      {!search && (
        <Button onClick={onCreate} disabled={creating} className="mt-6 gap-1.5">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          New Document
        </Button>
      )}
    </div>
  );
}
